import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Tab, PopupRef } from '@local/ui-infra';
import { filter, take } from 'rxjs';
import { generateId, isCollectionWidget } from '@local/common-web';
import { HomeTabs } from '@local/client-contracts';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { CollectionPopupService } from '../../../collections-page/services/collection-popup.service';
import { ShareOptionsPermissionsModel } from 'src/app/bar/components/share-options-permissions/share-options-permissions.component';
import { EventsService } from '@shared/services';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { AvatarItemModel } from 'src/app/bar/models/avatar-item.model';
import { HomeSettingsService } from 'src/app/bar/services/home-settings.service';
import { PostWidgetService } from 'src/app/bar/views/home-page/services/post-widget.service';
import { HomePinsService } from 'src/app/bar/services/home-pins.service';
import { cloneDeep, isEqual } from 'lodash';
import { HomeComponentFocused, HomePageService, MethodsList } from 'src/app/bar/services/home-page.service';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { getModifiers, isEnterKey, KeyName } from '@local/ts-infra';
import { stopEvent } from '@shared/utils/elements-util';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { HeaderWidgetService } from '../../services/header-widget.service';
import { AddToCollectionSelect } from 'src/app/bar/components/selected-item-popup/models/add-to-collection-select';
import { ShareOptionsService } from 'src/app/bar/services/share-options.service';
import { Constants } from '@local/common';
import { WorkspacesService } from 'src/app/bar/services';

@UntilDestroy()
@Component({
  selector: 'tabs-view-wrapper',
  templateUrl: './tabs-view-wrapper.component.html',
  styleUrls: ['./tabs-view-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabsViewWrapperComponent implements OnInit {
  private readonly COMPARE_FIELDS = ['id', 'name', 'icon', 'tooltip', 'disabled', 'editMode', 'shareOptions'];
  private readonly SHARE_TAB_MODEL: ShareOptionsPermissionsModel = {
    selectedOwnerOrAdminByRole: {
      enabled: true,
      label: 'Admin',
    },
    telemetryName: 'homepage_share',
    displayPermissionOption: false,
    warningMessage: 'For the best experience, make sure that users who have access to this tab also have access to the collections in it.',
    descriptionShareGroups: 'Select which group or member to share this Homepage tab with',
  };
  readonly staticActionTabs = [
    {
      name: 'New Tab',
      icon: 'icon-plus-thick',
      tooltip: 'New Tab',
      id: generateId(),
      disabled: false,
    },
  ];
  private _editModeTabId: string;
  private _tabs: HomeTabs.HomeTab[];
  movingTabs: HomeTabs.HomeTab[];

  currentTabId: string;
  currentTab: HomeTabs.HomeTab;
  avatarsList: AvatarItemModel[];
  staticTabs: HomeTabs.HomeTab[];
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  contextMenuOpened: boolean;

  //keyboard
  private readonly COMPONENT_FOCUSED_ID = HomeComponentFocused.TABS;
  private keyHandlerId: string;
  private componentFocused: boolean;
  selectedIndex: number;
  methodsList: MethodsList = [];
  @ViewChild('contextMenu', { read: ElementRef }) contextMenuRef: ElementRef;

  @Input() isOwnerOrAdmin: boolean;
  @Input() hasDelayTab: boolean;

  @Input() set tabs(val: HomeTabs.HomeTab[]) {
    if (!val) {
      return;
    }
    if (!this.stateChange(this.tabs, val)) {
      return;
    }
    this._tabs = cloneDeep(val);
    this.onTabsChange();
  }

  get tabs(): HomeTabs.HomeTab[] {
    return this._tabs;
  }

  get editModeTabId(): string {
    return this._editModeTabId;
  }

  set editModeTabId(val: string) {
    this._editModeTabId = val;
    this.updateMovingTabs();
    this.editMode.emit(!!val);
  }

  @Output() activeTabId = new EventEmitter<string>();
  @Output() action = new EventEmitter<string>();
  @Output() editMode = new EventEmitter<boolean>();
  @Output() tabCreated = new EventEmitter<HomeTabs.HomeTab>();

  constructor(
    private cdr: ChangeDetectorRef,
    private homeTabService: HomeTabsService,
    private homePinService: HomePinsService,
    private contextMenuService: ContextMenuService,
    private collectionPopupService: CollectionPopupService,
    private eventsService: EventsService,
    private collectionsService: CollectionsService,
    private homeSettingsService: HomeSettingsService,
    private postWidgetService: PostWidgetService,
    private homePageService: HomePageService,
    private keyboardService: KeyboardService,
    private avatarListService: AvatarListService,
    private headerWidgetService: HeaderWidgetService,
    private shareOptionsService: ShareOptionsService,
    private workspacesService: WorkspacesService
  ) {
    this.homePageService.currentTabId$.pipe(untilDestroyed(this)).subscribe((tabId) => this.onCurrentTabIdChange(tabId));

    this.homeTabService.createTab$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x)
      )
      .subscribe((newTab) => {
        this.tabCreated.emit(newTab);
      });
  }

  ngOnInit() {
    this.registerKeyboardHandler();
    this.homePageService.triggerComponentFocus$.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value.live === this.COMPONENT_FOCUSED_ID) {
        this.componentFocused = true;
        this.selectedIndex = value.step === 'next' ? 0 : this.methodsList.length - 1;
      } else {
        this.selectedIndex = null;
        this.componentFocused = false;
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  private onCurrentTabIdChange(id: string) {
    if (!id || this.currentTabId === id) {
      return;
    }
    this.currentTabId = id;
    this.onTabsChange();
  }

  private setMethodsList() {
    setTimeout(() => {
      this.methodsList = [{ id: 'tab', fn: () => {} }];
      if (this.isOwnerOrAdmin) {
        if (!this.currentTab?.disabled) {
          this.methodsList.push({ id: 'share', fn: () => this.handleCommand({ id: 'share' }, this.currentTab) });
        }
        this.methodsList.push({
          id: 'context_menu',
          fn: () => this.openContextMenu({ event: this.contextMenuRef?.nativeElement, tabId: this.currentTab.id }),
        });
      }
    }, 0);
  }

  private async onTabsChange() {
    if (!this.tabs || !this.currentTabId) {
      return;
    }
    this.currentTab = this.tabs.find((t) => t.id === this.currentTabId);
    if (!this.currentTab) {
      return;
    }
    this.movingTabs = this.tabs.filter((tab) => !tab?.disabled);
    this.staticTabs = this.tabs.filter((tab) => tab?.disabled).map(({ widgets, ...t }) => ({ ...t }));
    this.updateMovingTabs();
    this.avatarsList = await this.getAvatarsList();
    this.setMethodsList();
    this.cdr.markForCheck();
  }

  private updateMovingTabs() {
    this.movingTabs = this.movingTabs.map(({ widgets, ...t }) => ({ ...t, editMode: this.editModeTabId === t.id }));
  }

  private async getAvatarsList(): Promise<AvatarItemModel[]> {
    if (!this.currentTab) {
      return;
    }
    if (this.currentTab.disabled) {
      return [this.avatarListService.createWorkspaceAvatar()];
    }
    return this.avatarListService.createAvatarList(this.currentTab.accountId, this.currentTab.shareOptions, true);
  }

  async setActiveTab(id: string) {
    this.activeTabId.emit(id);
  }

  onTabAction(event) {
    switch (event.action) {
      case 'New Tab':
        this.addNewTab();
        break;
      case 'rename':
        this.renameTab(event);
        break;
    }
  }

  renameTab(event) {
    const index = this.movingTabs.findIndex((tab) => tab.id === event.tabId);
    if (index < 0) {
      return;
    }

    const model = event.event?.inputElement?.model;
    const noValueFromInput = !model || !model.length;
    const name = noValueFromInput ? this.movingTabs[index].name : model;
    this.homeTabService.update(this.movingTabs[index], [
      {
        type: 'Update',
        field: 'name',
        value: name,
      },
    ]);
    this.editModeTabId = null;
    this.movingTabs[index] = { ...this.movingTabs[index], name };
  }

  private stateChange(oldTabs: HomeTabs.HomeTab[], newTabs: HomeTabs.HomeTab[]) {
    if (!oldTabs || !newTabs || oldTabs.length !== newTabs.length) {
      return true;
    }
    const toComparableTab = (t: HomeTabs.HomeTab) => {
      const tab = {};
      this.COMPARE_FIELDS.forEach((f) => (tab[f] = t[f]));
      return tab;
    };
    const tabs1 = oldTabs.map((t) => toComparableTab(t));
    const tabs2 = newTabs.map((t) => toComparableTab(t));
    return !isEqual(tabs1, tabs2);
  }

  private async addNewTab() {
    const id = generateId();
    this.editModeTabId = id;
    await this.homeTabService.create(id);
  }

  async openContextMenu(event: any) {
    const index = this.tabs.findIndex((tab) => tab.id === event.tabId);
    if (index < 0 || !this.isOwnerOrAdmin) {
      return;
    }
    const items = await this.buildContextMenuItems(this.tabs[index]);
    this.contextMenuOpened = true;
    this.activeContextMenu = this.contextMenuService.open(
      { x: event.event.x, y: event.event.y },
      {
        items,
        onInvoke: (item: ContextMenuItem) => this.handleCommand(item, this.tabs[index]),
        minWidthItem: 124,
      },
      { position: 'below' }
    );
    this.activeContextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuOpened = false;
      this.cdr.markForCheck();
    });
  }

  private async buildContextMenuItems(tab: HomeTabs.HomeTab): Promise<ContextMenuItem[]> {
    const optionDisabledTab: ContextMenuItem[] = [];
    const disableCollections = await this.workspacesService.isFeatureFlagDisabled(Constants.DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG);
    if (!disableCollections) {
      optionDisabledTab.push({
        id: 'collection',
        text: 'Add Collection',
        command: { type: 'add-collection' },
        icon: { type: 'font-icon', value: 'icon-collection' },
      });
    }
    optionDisabledTab.push(
      {
        id: 'post',
        text: 'Add Post',
        command: { type: 'add-post' },
        icon: { type: 'font-icon', value: 'icon-create-widget' },
      },
      {
        id: 'header',
        text: 'Add Header',
        command: { type: 'add-header' },
        icon: { type: 'font-icon', value: 'icon-header' },
      }
    );
    return tab.disabled
      ? optionDisabledTab
      : [
          {
            id: 'rename',
            text: 'Rename',
            command: { type: 'rename-tab' },
            icon: { type: 'font-icon', value: 'icon-register' },
          },
          {
            id: 'share',
            text: 'Share',
            command: { type: 'share-tab' },
            icon: { type: 'font-icon', value: 'icon-members' },
          },
          ...optionDisabledTab,
          {
            id: 'delete',
            text: 'Delete',
            command: { type: 'delete-tab' },
            icon: { type: 'font-icon', value: 'icon-delete' },
          },
        ];
  }

  private shareTab(tab: HomeTabs.HomeTab) {
    const shareOptionsModel: ShareOptionsPermissionsModel = {
      title: `Share "${tab.name}" Tab`,
      updateGroupAndMembers: 'Groups and Members',
      accountId: tab.accountId,
      isShared: tab.isShared,
      shareOptions: tab.shareOptions,
      ...this.SHARE_TAB_MODEL,
    };

    this.shareOptionsService.openShareOptionPopup(shareOptionsModel, async (target, shareOptions) => {
      const permissionRead = shareOptions.permissions?.find((p) => p.scope === 'read');
      if (permissionRead) {
        shareOptions.permissions = [permissionRead];
      } else {
        shareOptions.permissions = [];
      }
      tab.shareOptions = shareOptions;
      this.avatarsList = await this.getAvatarsList();
      this.cdr.markForCheck();
      this.homeTabService.update(tab, [], shareOptions);
      this.eventsService.event(`tabs.tab_share`, {
        target,
        location: { title: 'home' },
      });
    });

    this.eventsService.event(`tabs.tab_share_open`, {
      location: { title: 'home' },
    });
  }

  async handleCommand(command: ContextMenuItem, tab: HomeTabs.HomeTab) {
    if (!tab) {
      return;
    }

    this.eventsService.event('home_tabs.edit', {
      target: command.id,
      location: { title: 'home' },
    });
    if (command.id === 'rename') {
      this.editModeTabId = tab.id;
      this.cdr.markForCheck();
    } else if (command.id === 'share') {
      this.shareTab(tab);
    } else if (command.id === 'collection') {
      this.collectionsService.openSelectPopup$.next({
        data: {
          title: `Add Collection to "${tab.name}" Tab`,
          itemsList: [],
          tabId: tab?.id,
          collections: this.collectionsService.collections,
          currentUser: this.collectionsService.currentUser,
        } as AddToCollectionSelect,
        type: 'add-collection-to-tab',
      });
    } else if (command.id === 'post') {
      this.postWidgetService.openNewPostPopup({ tab });
    } else if (command.id === 'header') {
      await this.headerWidgetService.createHeaderWidget(tab.id);
    } else if (command.id === 'delete') {
      if (tab.isShared) {
        const deletePopupRef = this.collectionPopupService.openDeleteTabPopup();
        deletePopupRef.compInstance.primaryButton.pipe(take(1)).subscribe(async () => {
          this.eventsService.event('home_tabs.delete', {
            target: 'delete',
            location: { title: 'home' },
          });
          await this.deleteTab(tab.id);
        });
        deletePopupRef.compInstance.secondaryButton.pipe(take(1)).subscribe(async () => {
          this.eventsService.event('home_tabs.delete', {
            target: 'cancel',
            location: { title: 'home' },
          });
        });
      } else {
        await this.deleteTab(tab.id);
      }
    }
  }

  private async deleteTab(tabId: string) {
    if (this.currentTabId === tabId) {
      this.currentTabId = 'home';
    }
    const tab = this.tabs.find((t) => t.id === tabId);
    this.tabs = this.tabs.filter((t) => t.id !== tabId);
    this.cdr.markForCheck();
    await this.homeTabService.delete(tabId);
    if (tab?.widgets) {
      tab.widgets.forEach((w) => {
        if (isCollectionWidget(w)) {
          const collection = this.collectionsService.collections.find((c) => c.id === w.collectionId);
          if (collection && collection.pinBy) {
            const pin = collection.pinBy.find((p) => p.widgetId === w.id);
            if (pin) {
              this.homePinService.delete(pin.id);
            }
          }
        }
      });
    }
  }

  async itemsOrderChange(tabs: Tab[]) {
    this.eventsService.event('home_tabs.drag', {
      location: { title: 'home' },
    });
    const tabsIds = tabs.map((t) => t.id);
    await this.homeSettingsService.updateTabs(tabsIds);
  }

  private registerKeyboardHandler() {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    }
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 7);
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (!this.componentFocused) return;
    const modifiers = getModifiers(keys);
    const key = keys[0];

    if (key === 'tab') {
      stopEvent(event);
      if (modifiers.length && modifiers[0] === 'shift') {
        if (this.selectedIndex === 0) {
          this.homePageService.keyboardComponentFocus$.next({ step: 'prev', live: this.COMPONENT_FOCUSED_ID });
        }
        this.selectedIndex--;
        this.cdr.markForCheck();
        return;
      }
      if (this.selectedIndex === this.methodsList.length - 1) {
        this.homePageService.keyboardComponentFocus$.next({ step: 'next', live: this.COMPONENT_FOCUSED_ID });
      } else {
        this.selectedIndex++;
      }
      this.cdr.markForCheck();
    }
    if (isEnterKey(key)) {
      stopEvent(event);
      this.methodsList[this.selectedIndex]?.fn();
    }
  }
}
